/**
 * Rich text document schema — DESIGN.md §13.4, §17, D7.
 *
 * Slate stores ProseMirror JSON, never user HTML. This module is the single
 * definition of what a valid document looks like: the API validates every
 * incoming body against it (§17 — "unknown nodes/marks rejected"), the web
 * editor is configured from the same node list, and the MCP markdown bridge
 * (§12.5) produces documents that must satisfy it too.
 *
 * Adding a node type here is the *only* correct way to extend rich text. A
 * node the editor can produce but this schema rejects is a data-loss bug.
 */
import { z } from 'zod';
/**
 * Marks — inline formatting. `link` is the only one carrying attributes.
 *
 * `underline` is here because of #73. TipTap's StarterKit has included the
 * Underline extension since v3, so the editor has been able to produce the
 * mark all along while this schema rejected it — and Slate's own notification
 * emails use it, so pasting one of our own emails into a comment produced a
 * document our own API refused, with a JSON pointer for an error message. That
 * is precisely the "node the editor can produce but this schema rejects is a
 * data-loss bug" this file warns about, one level down at the marks.
 */
export const markSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('bold') }),
    z.object({ type: z.literal('italic') }),
    z.object({ type: z.literal('strike') }),
    z.object({ type: z.literal('underline') }),
    z.object({ type: z.literal('code') }),
    z.object({
        type: z.literal('link'),
        attrs: z.object({
            href: z.string().max(2048),
            target: z.string().nullish(),
            rel: z.string().nullish(),
            class: z.string().nullish(),
        }),
    }),
]);
/**
 * Node names the schema accepts. Kept as data so the editor config and the
 * validator cannot drift apart.
 */
export const NODE_TYPES = [
    'doc',
    'paragraph',
    'text',
    'heading',
    'bulletList',
    'orderedList',
    'listItem',
    'taskList',
    'taskItem',
    'blockquote',
    'codeBlock',
    'horizontalRule',
    'hardBreak',
    'image',
    'mention',
    'cardRef',
    'table',
    'tableRow',
    'tableCell',
    'tableHeader',
];
export const docNodeSchema = z.lazy(() => z.object({
    type: z.enum(NODE_TYPES),
    attrs: z.record(z.unknown()).optional(),
    content: z.array(docNodeSchema).optional(),
    marks: z.array(markSchema).optional(),
    text: z.string().optional(),
}));
/** A whole document. The root is always a `doc` node. */
export const richTextSchema = z
    .object({
    type: z.literal('doc'),
    content: z.array(docNodeSchema).optional(),
})
    .strict();
/** The canonical empty document — what an untouched description stores. */
export function emptyDoc() {
    return { type: 'doc', content: [] };
}
/** A document containing one paragraph of plain text. */
export function textDoc(text) {
    if (text === '')
        return emptyDoc();
    return {
        type: 'doc',
        content: text.split(/\n{2,}/).map((para) => ({
            type: 'paragraph',
            content: para
                .split('\n')
                .flatMap((line, i) => i === 0
                ? line
                    ? [{ type: 'text', text: line }]
                    : []
                : line
                    ? [{ type: 'hardBreak' }, { type: 'text', text: line }]
                    : [{ type: 'hardBreak' }]),
        })),
    };
}
export function isEmptyDoc(doc) {
    if (!doc?.content || doc.content.length === 0)
        return true;
    return extractText(doc).trim() === '' && !containsNode(doc, ['image', 'horizontalRule', 'table']);
}
function containsNode(node, types) {
    if (types.includes(node.type))
        return true;
    return (node.content ?? []).some((child) => containsNode(child, types));
}
/**
 * Extract plain text — the source of the `*_text` columns that back full-text
 * search (§5) and the plain-text alternative on every email (§11.3).
 *
 * Block boundaries become newlines so search phrases don't run words together
 * across paragraphs, and mentions/card refs render as the text a human typed
 * (`@Sarah`, `#1042`) so searching for either finds the card.
 *
 * Breaks are *requested* rather than emitted, and the largest pending request
 * wins when the next text arrives. That is what keeps a paragraph inside a
 * list item from double-spacing the list while paragraphs at the top level
 * still get the blank line between them that email rendering needs.
 */
export function extractText(node) {
    const state = { out: [], pendingBreak: 0 };
    walk(node, state, null);
    return state.out.join('').trim();
}
/** Blocks that stand alone and read better separated by a blank line. */
const PARAGRAPH_BLOCKS = new Set(['paragraph', 'heading', 'blockquote', 'codeBlock']);
/** Blocks that are items in a sequence — one newline between them. */
const ITEM_BLOCKS = new Set([
    'listItem',
    'taskItem',
    'tableRow',
    'bulletList',
    'orderedList',
    'taskList',
    'table',
]);
/** Containers whose children are items, so their inner paragraphs stay tight. */
const TIGHT_PARENTS = new Set(['listItem', 'taskItem', 'tableCell', 'tableHeader']);
function emit(state, text) {
    if (text === '')
        return;
    if (state.pendingBreak > 0 && state.out.length > 0) {
        state.out.push('\n'.repeat(state.pendingBreak));
    }
    state.pendingBreak = 0;
    state.out.push(text);
}
function requestBreak(state, level) {
    if (state.out.length === 0)
        return;
    if (level > state.pendingBreak)
        state.pendingBreak = level;
}
function walk(node, state, parent) {
    switch (node.type) {
        case 'text':
            emit(state, node.text ?? '');
            return;
        case 'hardBreak':
            requestBreak(state, 1);
            return;
        case 'horizontalRule':
            requestBreak(state, 2);
            return;
        case 'mention': {
            const label = typeof node.attrs?.label === 'string' ? node.attrs.label : '';
            emit(state, `@${label}`);
            return;
        }
        case 'cardRef': {
            const number = node.attrs?.number;
            emit(state, `#${typeof number === 'number' || typeof number === 'string' ? number : ''}`);
            return;
        }
        case 'image': {
            // Alt text is real content for search; the filename is a useful fallback.
            const alt = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';
            const name = typeof node.attrs?.filename === 'string' ? node.attrs.filename : '';
            const label = alt || name;
            if (label)
                emit(state, `[${label}]`);
            return;
        }
        default:
            break;
    }
    if (node.type === 'tableCell' || node.type === 'tableHeader') {
        // Cells sit on one line, tab-separated, so a row reads as a row.
        if (state.out.length > 0 && state.pendingBreak === 0)
            state.out.push('\t');
    }
    for (const child of node.content ?? [])
        walk(child, state, node.type);
    if (PARAGRAPH_BLOCKS.has(node.type)) {
        requestBreak(state, parent !== null && TIGHT_PARENTS.has(parent) ? 1 : 2);
    }
    else if (ITEM_BLOCKS.has(node.type)) {
        requestBreak(state, 1);
    }
}
//# sourceMappingURL=schema.js.map